// Shared reception-related interfaces
import type {LanguageProgress} from '../constants/languages';
export interface Instructor {
  id: number;
  name: string;
  full_name?: string;
}
export interface Group {
  delivery_mode?: string | null;
  meeting_link?: string | null;
  delivery_reconciliation_note?: string | null;
  id: number;
  group_code: string;
  group_name: string;
  state: string;
  course_name?: string | null;
  begin_date?: string | null;
  end_date?: string | null;
  schedule_days?: string[] | null;
  start_time?: string | null;
  end_time?: string | null;
  session_duration_minutes?: number | null;
  number_of_sessions?: number | null;
  instructor?: Instructor | null;
  instructor_id?: number | null;
  mentor?: Instructor | null;
  created_at?: string;
  language?: string | null;
  level?: string | null;
  branch?: string | null;
  // Quarter fields
  linkedin_link?: string | null;
  whatsapp_link?: string | null;
  room?: string | null;
  required_students?: number | null;
  max_capacity?: number | null;
  reserved?: number;
  remaining?: number;
}
export interface StudentGroupBrief {
  id: number;
  code: string;
  name: string;
  course?: string | null;
  student_status?: string | null;
  status?: string | null;
}
export interface Student {
  responsible_employee?: { id: number; name: string; department: string; automatic: boolean } | null;
  delivery_mode?: string | null;
  registration_branch?: string | null;
  id: number;
  student_code: string;
  full_name: string;
  email?: string;
  registration_status?: 'registered' | 'payment_pending' | 'approved';
  is_approved?: boolean;
  is_active?: boolean;
  needs_password_setup?: boolean;
  groups?: StudentGroupBrief[];
  national_id?: string;
  passport_number?: string;
  gender?: string;
  graduation_year?: number;
  current_job?: string;
  mobile?: string;
  whatsapp?: string;
  phone?: string;
  educational_qualification?: string;
  address?: string;
  date_of_birth?: string;
  age?: number;
  branch?: string | null;
  progress_status?: string;
  completed_courses?: string[];
  created_at?: string;
  profile_completed?: boolean;
  profile_image_url?: string | null;
  whatsapp_url?: string | null;
  intended_course?: string | null;
  courses?: string[];
  is_google?: boolean;
  google_followup_done?: boolean;
  has_duplicate_contact?: boolean;
  duplicate_email_count?: number;
  duplicate_phone_count?: number;
  placement_completed_at?: string | null;
  current_level?: string | null;
  language_progress?: LanguageProgress[];
  language_package_total_levels?: number | null;
  language_package_consumed_levels?: number | null;
  language_package_start_level?: string | null;
}
export interface StudentMember {
  id: number;
  membership_id: number;
  full_name: string;
  student_code?: string;
}
export interface AvailableStudent {
  id: number;
  full_name: string;
  student_code?: string;
  branch?: string;
  course?: string;
  intended_course?: string;
  phone?: string;
}
export interface Invoice {
  id: number;
  amount: number;
  remaining_amount?: number | null;
  issued_at?: string;
  total_amount?: number;
  notes?: string | null;
}
// Lightweight types for search/selection components
export interface StudentLite {
  id: number;
  full_name: string;
}
export interface GroupLite {
  id: number;
  group_code?: string;
  code?: string;
  language?: string;
  level?: string;
}

// ReceptionStudents specific types
export interface RealtimeStudentMsgBase {
  event?: string;
  event_type?: string;
  student_id?: number;
  full_name?: string;
  student_code?: string;
  registration_status?: 'registered' | 'payment_pending' | 'approved';
  is_approved?: boolean;
  profile_completed?: boolean;
  id?: number;
  progress_status?: string;
  group_id?: number;
  group_code?: string;
  group_name?: string;
  from_group_id?: number;
  to_group_id?: number;
  to_group_code?: string;
  to_group_name?: string;
  kept_source_group?: boolean;
  language_progression?: boolean;
  // Optional granular changes payload for updates (e.g., branch transfer, course change)
  changes?: {
    branch?: string | null;
    intended_course?: string | null;
    placement_completed_at?: string | null;
    [k: string]: unknown;
  };
  payload?: {
    student_id?: number | string;
    [k: string]: unknown;
  };
}

export interface DeleteStudentJsonResp {
  status?: string;
  reason?: string;
  remaining?: number | string;
  groups?: { id: number; code: string; name: string }[];
  error_text?: string;
}

export interface StudentsApiResp {
  students: Student[];
  page: number;
  page_size: number;
  pages: number;
  total: number;
}

export interface NavCounts {
  registered?: number;
  payment_pending?: number;
  pending?: number;
  waiting?: number;
  ready?: number;
  finished?: number;
  refunded?: number;
  didnt_complete?: number;
  groups?: number;
  inactive?: number;
  requests?: number;
  course_interests?: number;
  flags: Record<string, number>;
}

export const STUDENT_LIFECYCLE_STATUSES = [
  'pending',
  'registered',
  'payment_pending',
  'ready',
  'waiting',
  'didnt_complete',
  'refund',
  'finished',
  'inactive',
] as const;

export type StudentLifecycleStatus = typeof STUDENT_LIFECYCLE_STATUSES[number];
export type StatusCategory = 'all' | StudentLifecycleStatus;
export type ProgressFlag = string;

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const ROW_HEIGHT = 60;
export const DEDUP_WINDOW_MS = 4000;

// GroupsPage specific types
export interface GroupForm {
  max_capacity?: number | "";
  delivery_mode?: string;
  meeting_link?: string;
  group_code: string;
  group_name: string;
  state: string;
  course_name: string;
  begin_date: string;
  start_time: string;
  end_time: string;
  days: Set<string>;
  session_duration_minutes: number | "";
  number_of_sessions: string;
  instructor_id: string;
  mentor_id: string;
  level: string;
  branch: string;
}

export const COURSE_HOURS: Record<string, number> = {
  "Professional Data Analysis Diploma": 130,
  "Data Science & AI": 180,
  "AI Agents & Automation Diploma": 150,
  "AI Engineering Diploma": 150,
  "Computer Vision Diploma": 120,
  "Full-Stack Web Development with React Diploma": 220,
  "Full-Stack Web Development with Angular Diploma": 250,
  "Flutter Mobile App Development Diploma": 100,
  "Graphic Design with AI Diploma": 60,
  "UI/UX Design with AI Diploma": 60,
  "SOC Analyst Diploma": 160,
  "Web Penetration Testing Diploma": 120,
  "Mobile Penetration Testing Diploma": 120,
  "Malware Analysis & Reverse Engineering Diploma": 120,
  "Digital Forensics Diploma": 120,
  "Human Resources Management Diploma": 30,
  "ICDL Certification Program": 32,
  "Microsoft Office Specialist — MOS Program": 36,
  "English Language Program — 3 Levels": 120,
  "German Language Program — 3 Levels": 16,
};
